<script lang="ts">
  import { t } from '$i18n/index';
  import type { MessageKey } from '$i18n/index';
  import { navigate } from '$ui/router';
  import { app } from '$ui/app-state.svelte';
  import { catchesOn } from '$domain/reducers/catches';
  import { DEFAULT_TRIGGER_TAGS } from '$domain/comeback';

  /**
   * A plain record of today, for verification and for the user's own memory.
   *
   * Deliberately not a scoreboard: no total framed as a target, no comparison
   * with yesterday, no streak, no trend. Catch counts may rise in the early
   * weeks as awareness increases (spec §10) and this screen must never make
   * that read as a worsening score. I4's progress view is where trends belong,
   * with the framing that makes them honest.
   */
  const today = $derived(catchesOn(app.derived.catches, new Date()));

  function timeOf(iso: string): string {
    return iso.slice(11, 16);
  }

  function tagLabel(tag: string): string {
    return (DEFAULT_TRIGGER_TAGS as readonly string[]).includes(tag)
      ? t(`tag.${tag}` as MessageKey)
      : tag;
  }

  function outcomeLabel(resolution: string | null): string {
    if (resolution === null) return t('today.unresolved');
    return t(`resolve.${resolution}` as MessageKey);
  }
</script>

<main>
  <h1>{t('today.title')}</h1>

  {#if today.length === 0}
    <p class="empty">{t('today.empty')}</p>
  {:else}
    <ul>
      {#each today as record (record.catchId)}
        <li>
          <span class="time">{timeOf(record.occurredAt)}</span>
          <span class="tag">{tagLabel(record.triggerTag)}</span>
          <span class="outcome">{outcomeLabel(record.resolution)}</span>
        </li>
      {/each}
    </ul>
  {/if}

  <button type="button" class="back" onclick={() => navigate('catch')}>{t('today.back')}</button>
</main>

<style>
  main {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-6) var(--space-5);
  }

  h1 {
    font-size: var(--step-1);
    font-weight: 500;
  }

  .empty {
    color: var(--c-ink-muted);
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    flex: 1;
  }

  li {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: var(--space-3);
    align-items: baseline;
    padding-bottom: var(--space-3);
    border-bottom: 1px solid var(--c-line);
  }

  .time {
    color: var(--c-ink-faint);
    font-variant-numeric: tabular-nums;
    font-size: var(--step--1);
  }

  .outcome {
    color: var(--c-ink-muted);
    font-size: var(--step--1);
    text-align: right;
  }

  .back {
    align-self: center;
    color: var(--c-ink-faint);
    font-size: var(--step--1);
    padding: var(--space-2) var(--space-4);
  }
</style>
